import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Mailing } from '../api/client'
import { fmtInt } from '../lib/format'

const STATUS_TEXT_TONE: Record<Mailing['status'], string> = {
  planned: 'text-paper-dim',
  mailed: 'text-radar-cyan',
  closed: 'text-radar-green',
}

function fmtUsd(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

export function MailingsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['mailings'],
    queryFn: () => api.get<{ items: Mailing[] }>('/api/mailings'),
    refetchInterval: 30_000,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Execution · direct mail</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Mailings
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">postal cohorts</span>
          </h1>
        </div>
      </header>

      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}
      {!isLoading && data && data.items.length === 0 && (
        <div className="label text-paper-trace p-6">No mailings yet.</div>
      )}

      {data && data.items.length > 0 && (
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-4 font-normal text-left">Mailing</th>
                <th className="label py-2 px-3 font-normal text-left">Title</th>
                <th className="label py-2 px-3 font-normal text-left">Mail date</th>
                <th className="label py-2 px-3 font-normal text-right">Recipients</th>
                <th className="label py-2 px-3 font-normal text-right">Matched</th>
                <th className="label py-2 px-3 font-normal text-right">Conversions</th>
                <th className="label py-2 px-3 font-normal text-right">Revenue</th>
                <th className="label py-2 px-3 font-normal text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <tr key={m.id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <td className="px-4 py-2">
                    <Link to={`/mailings/${m.id}`} className="text-paper hover:text-amber-glow">
                      {m.name}
                    </Link>
                    <div className="text-[10px] text-paper-trace">{m.slug}</div>
                  </td>
                  <td className="px-3 py-2 text-paper-dim">{m.publication_name ?? '—'}</td>
                  <td className="px-3 py-2 text-paper-dim tabular-nums">{m.mail_date}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(m.recipient_count)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-paper-dim">{fmtInt(m.matched_count)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-glow">{fmtInt(m.conversions_count)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(m.conversions_revenue)}</td>
                  <td className={`px-3 py-2 ${STATUS_TEXT_TONE[m.status]}`}>{m.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
